import React from 'react';
import { Box, Typography, Grid, useTheme, alpha, Container, Card } from '@mui/material';
import { StyledTextField } from './index';
import { usePermissions } from '../../../utils/permissions';
import { Phone as PhoneIcon } from '@mui/icons-material';

const ContactInformation = ({ 
  companyInfo, 
  handleInputChange, 
  loading, 
  requiredFields, 
  validationErrors 
}) => {
  const theme = useTheme();
  const { hasPermission, canAccessPage, canEditPage } = usePermissions();
  
  // Permission check for contact information access
  if (!canAccessPage('contact_information')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <PhoneIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Contact Information Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access the Contact Information settings. Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  const isFieldRequired = (fieldName) => {
    return requiredFields['contact-info']?.includes(fieldName) || false;
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const canEditSettings = canEditPage('contact_information');

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        p: 4,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.2rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Contact Information
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '1rem',
            maxWidth: '400px'
          }}>
            Manage your primary business contact details and communication channels
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Phone Number" 
            name="phone"
            value={companyInfo.phone} 
            onChange={handleInputChange} 
            required={isFieldRequired('phone')}
            error={!!getFieldError('phone')}
            helperText={getFieldError('phone')}
            disabled={loading || !canEditSettings}
            placeholder="(123) 456-7890"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Fax Number" 
            name="fax"
            value={companyInfo.fax} 
            onChange={handleInputChange} 
            disabled={loading || !canEditSettings}
            placeholder="(123) 456-7891"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Email Address" 
            name="email"
            type="email"
            value={companyInfo.email} 
            onChange={handleInputChange} 
            required={isFieldRequired('email')}
            error={!!getFieldError('email')}
            helperText={getFieldError('email')}
            disabled={loading || !canEditSettings}
            placeholder="contact@yourcompany.com"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField 
            label="Website" 
            name="website"
            value={companyInfo.website} 
            onChange={handleInputChange} 
            required={isFieldRequired('website')}
            error={!!getFieldError('website')}
            helperText={getFieldError('website')}
            disabled={loading || !canEditSettings}
            placeholder="https://www.yourcompany.com"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactInformation;